import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Calendar } from 'lucide-react';
import { posApi } from '../../services/api';
import toast from 'react-hot-toast';
import BankAccountSelect from '../common/BankAccountSelect';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { useShiftStore } from '../../store/shiftStore';

interface AddCustomerPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: {
    name: string;
    customer_name: string;
    outstanding_balance?: number;
  } | null;
}

export default function AddCustomerPaymentModal({ isOpen, onClose, customer }: AddCustomerPaymentModalProps) {
  useEscapeToClose(onClose, isOpen);
  const openShift = useShiftStore((s) => s.openShift);
  const queryClient = useQueryClient();
  const maxAmount = Number((customer?.outstanding_balance || 0).toFixed(2));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (maxAmount > 0) {
        setAmount(String(maxAmount));
      } else {
        setAmount('');
      }
    } else {
      setAmount('');
      setReferenceNo('');
      setPaymentMethod('Cash');
      setBankAccount('');
    }
  }, [isOpen, maxAmount]);

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });


  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!customer?.name) throw new Error('Customer is missing');
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error('Please enter a valid amount');
      }
      if (paymentMethod === 'Bank' && !bankAccount) {
        throw new Error('Select a bank');
      }
      const bank = Array.isArray(banks) ? banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount) : undefined;

      await posApi.addCustomerPayment({
        customer: customer.name,
        date,
        payment_method: paymentMethod,
        bank_account: paymentMethod === 'Bank' ? bankAccount : '',
        bank_name: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        account: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        amount: numAmount,
        reference_no: referenceNo,
        ...(openShift ? { pos_shift: openShift.name } : {}),
      });
    },
    onSuccess: () => {
      toast.success('Payment received successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setAmount('');
      setReferenceNo('');
      setPaymentMethod('Cash');
      setBankAccount('');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to receive payment');
    },
  });

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900">Receive Payment</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">
            Receiving payment for <span className="font-medium text-gray-900">{customer.customer_name}</span>
            {' '}(outstanding balance).
          </p>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {maxAmount < 0 ? 'Advance:' : 'Outstanding:'} Rs. {Math.abs(maxAmount).toLocaleString()}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
            <select
              value={paymentMethod}
              onChange={(e) => {
                const next = e.target.value;
                setPaymentMethod(next);
                if (next === 'Bank' && !bankAccount && Array.isArray(banks) && banks.length) {
                  const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                  setBankAccount(def.name);
                }
                if (next !== 'Bank') setBankAccount('');
              }}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
            </select>
          </div>

          {paymentMethod === 'Bank' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
              <BankAccountSelect value={bankAccount} onChange={setBankAccount} banks={banks} required />
            </div>
          )}


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs) *</label>
            <input
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g. 5000"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference No (Optional)</label>
            <input
              type="text"
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g. TRX-12345"
            />
          </div>
        </div>

        <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !amount}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
